import { z } from 'zod';
// Per-`(provider, kind)` config contracts — the typing mechanism ADR-024 locks.
//
// THE EXTENSION CONTRACT: adding a provider is a new entry here plus a send/call
// adapter, and **zero schema migration**. If a provider ever needs an `ALTER TABLE`,
// the model failed. A provider is shipped code, never user-created reference data.
//
// Each contract is THREE shapes, deliberately distinct — conflating them is how
// secrets leak into responses:
//   write  — what an admin submits (carries the raw secret, exactly once)
//   stored — what lands in `integrations.config` jsonb (secret ENCRYPTED at rest,
//            plus whatever only the provider can tell us, read back at save time)
//   view   — what any response may carry (NO secret, by construction)
// ── Providers + kinds ────────────────────────────────────────────────────────
export const INTEGRATION_PROVIDERS = ['telegram'];
export const IntegrationProviderSchema = z.enum(INTEGRATION_PROVIDERS);
export const INTEGRATION_KINDS = ['bot', 'destination', 'service'];
export const IntegrationKindSchema = z.enum(INTEGRATION_KINDS);
// ── telegram:bot ─────────────────────────────────────────────────────────────
// BotFather issues `<bot_id>:<secret>` — e.g. `7100200300:AAF…`. The shape check
// is a fast, friendly fail on a mis-paste (a trailing quote, half a token); it is
// NOT the validation that matters. Authority is Telegram's own getMe, called at
// save time — only it can say whether the token is live, and only it yields the
// username. Kept loose on the secret's length/alphabet so a future BotFather
// format change doesn't reject valid tokens.
const telegramBotToken = z
    .string()
    .trim()
    .min(20)
    .max(120)
    .regex(/^\d{5,}:[A-Za-z0-9_-]{20,}$/, 'not a Telegram bot token — expected <bot_id>:<secret> from BotFather');
/** What the admin submits — the raw token, and nothing else. Everything else about
 *  the bot is READ BACK from Telegram rather than typed (see the stored shape). */
export const TelegramBotConfigWriteSchema = z.object({ bot_token: telegramBotToken }).strict();
/** What lands in the jsonb.
 *
 *  `bot_username` is load-bearing, not decoration: EVERY onboarding deep link is
 *  built from it (`t.me/<bot_username>?start=…`). It is fetched from getMe and never
 *  typed by a human — one typo would silently kill every invite in the project.
 *  `bot_id` is the bot's stable identity and the guard against the same bot being
 *  registered on two projects. Telegram usernames cap at 32 chars. */
export const TelegramBotConfigStoredSchema = z
    .object({
    bot_token_encrypted: z.string().min(1),
    bot_id: z.number().int().positive(),
    bot_username: z.string().trim().min(1).max(32),
})
    .strict();
/** What responses may carry. The encrypted token is absent BY CONSTRUCTION —
 *  there is no field for it, so a projection bug cannot leak it. */
export const TelegramBotConfigViewSchema = z
    .object({
    type: z.literal('telegram:bot'),
    bot_id: z.number().int().positive(),
    bot_username: z.string().max(32),
})
    .strict();
// ── The registry ─────────────────────────────────────────────────────────────
/** Every contract, keyed by `provider:kind`. One entry per supported combination —
 *  a `(provider, kind)` pair absent here is rejected at the route as unsupported,
 *  which is what keeps `config` jsonb from becoming a junk drawer. */
export const INTEGRATION_CONFIG_CONTRACTS = {
    'telegram:bot': {
        write: TelegramBotConfigWriteSchema,
        stored: TelegramBotConfigStoredSchema,
        view: TelegramBotConfigViewSchema,
    },
};
export function contractKey(provider, kind) {
    return `${provider}:${kind}`;
}
export function isSupportedContract(provider, kind) {
    return contractKey(provider, kind) in INTEGRATION_CONFIG_CONTRACTS;
}
/** The union every response's `config` travels as. `z.discriminatedUnion` on the
 *  synthetic `type` field means a client narrows in one step, and a second provider
 *  is one more member — no machinery to build later. */
export const IntegrationConfigViewSchema = z.discriminatedUnion('type', [TelegramBotConfigViewSchema]);
